/**
 * 提案メール作成 hook
 * マッチング結果から提案メールを AI 生成し、Gmail API 経由で送信する.
 */
import { fetchAuthSession } from "@aws-amplify/auth";
import type { EmailConnection } from "hooks/use-email-integrations-api";
import type { MatchingResultItem } from "hooks/use-free-matching-api";
import type { EmailDocument } from "hooks/useEmailsRestApi";
import { restClient } from "lib/rest-client";
import { useCallback, useRef, useState } from "react";

type EmailConnectionListResponse = { items: EmailConnection[] };

// REST API ベース URL
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";
const REST_API_BASE_URL = `${API_ENDPOINT}/api`;

// =====================================
// Types
// =====================================

export interface ProposalDraft {
  resultId: string;
  matchedSubject: string;
  totalScore: number;
  aiHighlights: string[] | null;
  fromEmail: string;
  fromName: string;
  toEmail: string;
  toName: string;
  subject: string;
  body: string;
  /** 自社マージン（万円）. AI生成時の単価補正に使用 */
  margin: number;
}

/** マージンのデフォルト値（万円） */
const DEFAULT_MARGIN = 5;

interface SendProposalResponse {
  success: boolean;
  message: string;
  messageId: string | null;
}

export interface UseProposalComposerResult {
  draft: ProposalDraft | null;
  isGenerating: boolean;
  isSending: boolean;
  error: string | null;
  startProposal: (result: MatchingResultItem, email: EmailDocument) => void;
  setField: <K extends keyof ProposalDraft>(
    field: K,
    value: ProposalDraft[K],
  ) => void;
  regenerate: () => void;
  sendProposal: () => Promise<boolean>;
  resetDraft: () => void;
  cancelGeneration: () => void;
}

/**
 * 提案メール作成を管理する hook
 * @return {UseProposalComposerResult} 提案メール作成の状態と操作関数
 */
export function useProposalComposer(): UseProposalComposerResult {
  const [draft, setDraft] = useState<ProposalDraft | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  /**
   * 提案メール本文を AI 生成する（共通ロジック）
   * @param {string} resultId マッチング結果ID
   * @param {number} margin 自社マージン（万円）
   * @param {string | null} [existingSenderEmail] 既存の送信者メール（undefined なら取得）
   * @param {string | null} [existingSenderName] 既存の送信者名
   * @return {Promise<void>}
   */
  const runGenerate = useCallback(
    async (
      resultId: string,
      margin: number,
      existingSenderEmail?: string,
      existingSenderName?: string,
    ): Promise<void> => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      setError(null);
      setIsGenerating(true);
      // 本文をクリアして再生成に備える
      setDraft(prev => (prev ? { ...prev, body: "" } : prev));

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) {
          setError("認証トークンの取得に失敗しました");
          setIsGenerating(false);
          return;
        }

        let senderEmail = existingSenderEmail ?? "";
        let senderName = existingSenderName ?? "";

        if (!senderEmail) {
          let active: EmailConnection | null = null;
          try {
            const response = await restClient.get<EmailConnectionListResponse>(
              "/email-integrations",
            );
            active =
              (response.items ?? []).find(
                c => c.provider === "gmail" && c.status === "connected",
              ) ?? null;
          } catch {
            // 連携取得に失敗した場合はフォールバック
          }
          senderEmail = active?.emailAddress ?? "";
          senderName = active?.displayName ?? active?.emailAddress ?? "";

          setDraft(prev =>
            prev
              ? { ...prev, fromEmail: senderEmail, fromName: senderName }
              : prev,
          );
        }

        if (!senderEmail) {
          setError(
            "Gmail 連携が必要です。連携設定からアカウントを連携してください。",
          );
          setIsGenerating(false);
          return;
        }

        const response = await fetch(
          `${REST_API_BASE_URL}/matching/results/${resultId}/generate-proposal`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              // biome-ignore lint/style/useNamingConvention: HTTP header
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              fromName: senderName,
              fromEmail: senderEmail,
              margin,
            }),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          setError(`AI生成エラー: HTTP ${response.status}`);
          setIsGenerating(false);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          setError("レスポンスの読み取りに失敗しました");
          setIsGenerating(false);
          return;
        }

        const decoder = new TextDecoder();
        let buffer = "";

        let done = false;
        while (!done) {
          const readResult = await reader.read();
          done = readResult.done;
          if (done) break;

          buffer += decoder.decode(readResult.value, { stream: true });

          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const dataStr = line.slice(6);

            try {
              const data = JSON.parse(dataStr);

              if (data.type === "meta") {
                setDraft(prev =>
                  prev
                    ? {
                        ...prev,
                        toEmail: data.toEmail || prev.toEmail,
                        toName: data.toName || prev.toName,
                        subject: data.subject || prev.subject,
                      }
                    : prev,
                );
              } else if (data.type === "chunk") {
                setDraft(prev =>
                  prev ? { ...prev, body: prev.body + data.text } : prev,
                );
              } else if (data.type === "done") {
                setIsGenerating(false);
              } else if (data.type === "error") {
                setError(data.message || "AI生成に失敗しました");
                setIsGenerating(false);
              }
            } catch {
              // JSON パース失敗は無視
            }
          }
        }

        setIsGenerating(false);
      } catch (e) {
        if ((e as Error).name !== "AbortError") {
          setError(e instanceof Error ? e.message : "AI生成に失敗しました");
          setIsGenerating(false);
        }
      } finally {
        abortRef.current = null;
      }
    },
    [],
  );

  const startProposal = useCallback(
    (result: MatchingResultItem, email: EmailDocument) => {
      setDraft({
        resultId: result.id,
        matchedSubject: result.matchedSubject,
        totalScore: result.totalScore,
        aiHighlights: result.aiHighlights,
        fromEmail: "",
        fromName: "",
        toEmail: email.from.address,
        toName: email.from.name,
        subject: `Re: ${email.subject || ""}`,
        body: "",
        margin: DEFAULT_MARGIN,
      });
      void runGenerate(result.id, DEFAULT_MARGIN);
    },
    [runGenerate],
  );

  const regenerate = useCallback(() => {
    if (!draft) return;
    void runGenerate(
      draft.resultId,
      draft.margin,
      draft.fromEmail,
      draft.fromName,
    );
  }, [draft, runGenerate]);

  const setField = useCallback(
    <K extends keyof ProposalDraft>(field: K, value: ProposalDraft[K]) => {
      setDraft(prev => (prev ? { ...prev, [field]: value } : prev));
      // 送信元メールアドレスが変更された場合、名前の更新は呼び出し元で行う
    },
    [],
  );

  const sendProposal = useCallback(async (): Promise<boolean> => {
    if (!draft) return false;

    setIsSending(true);
    setError(null);

    try {
      const response = await restClient.post<SendProposalResponse>(
        `/matching/results/${draft.resultId}/send-proposal`,
        {
          fromName: draft.fromName,
          fromEmail: draft.fromEmail,
          toEmail: draft.toEmail,
          toName: draft.toName,
          subject: draft.subject,
          body: draft.body,
        },
      );

      if (!response.success) {
        setError(response.message || "送信に失敗しました");
        return false;
      }

      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "送信に失敗しました");
      return false;
    } finally {
      setIsSending(false);
    }
  }, [draft]);

  const resetDraft = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setDraft(null);
    setError(null);
    setIsGenerating(false);
    setIsSending(false);
  }, []);

  const cancelGeneration = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsGenerating(false);
  }, []);

  return {
    draft,
    isGenerating,
    isSending,
    error,
    startProposal,
    setField,
    regenerate,
    sendProposal,
    resetDraft,
    cancelGeneration,
  };
}
